import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

const SERVICES = [
  {
    id: "sante",
    title: "Mutuelle & Prévoyance",
    badge: "Bien-être & Soins",
    description:
      "Remboursements renforcés en dentaire, optique et hospitalisation, sans dépassements d'honoraires imprévus.",
    highlights: ["Tiers payant national", "Téléconsultation 24/7", "Médecine douce"],
    img: "https://images.unsplash.com/photo-1579538506280-84e461ad0077?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHxfHxmcmVuY2glMjBmYW1pbHklMjBob21lJTIwc21pbGV8ZW58MHx8fHwxNzg5NzEwMzQ0fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7",
  },
  {
    id: "pro",
    title: "Professionnels & Entreprises",
    badge: "Solutions Pro",
    description:
      "RC Pro, Multirisque, Décennale et Prévoyance Dirigeant pour artisans, commerçants et PME exigeants.",
    highlights: ["Protection juridique", "Perte d'exploitation", "Homme clé"],
    img: "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBicm9rZXIlMjBjb25zdWx0aW5nJTIwY2xpZW50fGVufDB8fHx8MTc4OTcxMDM0NHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
  },
  {
    id: "auto",
    title: "Assurance Automobile",
    badge: "Protection Route",
    description:
      "Couverture tous risques sur-mesure, assistance 0 km, véhicule de remplacement premium et garanties conducteur étendues.",
    highlights: ["Assistance 0 km 24/7", "Bonus récompensé", "Valeur à neuf"],
    img: "https://thumb.wikimedia.org/wikipedia/commons/thumb/8/88/Fiat_500_yellow.jpg/1920px-Fiat_500_yellow.jpg",
    span: "lg:col-span-4",
  },
  {
    id: "habitation",
    title: "Habitation & Patrimoine",
    badge: "Protection Foyer",
    description:
      "Protection intégrale pour résidence principale, secondaire ou investissement locatif, avec prise en charge rapide des sinistres.",
    highlights: ["Vol & vandalisme", "Objets de valeur", "RC famille"],
    img: "https://images.pexels.com/photos/31517287/pexels-photo-31517287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-4",
  },
  {
    id: "vie",
    title: "Vie & Épargne Retraite",
    badge: "Patrimoine",
    description:
      "Optimisation fiscale, transmission de capital sécurisée et accompagnement personnalisé par votre conseiller dédié.",
    highlights: ["Fiscalité avantageuse", "Multisupports", "Transmission protégée"],
    img: "https://images.pexels.com/photos/3807572/pexels-photo-3807572.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-4",
  },
];

export default function Services() {
  return (
    <section id="expertises" data-testid="services-section" className="relative py-24 sm:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 sm:mb-20 max-w-3xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber/90 mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-amber/60" /> Nos expertises
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-ivory text-balance">
            Cinq domaines, une même exigence&nbsp;:{" "}
            <span className="italic text-amber">votre sérénité.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6" data-testid="insurance-services-grid">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.id}
              href={`/${s.id === "sante" ? "mutuelle-sante" : s.id === "pro" ? "assurance-professionnelle" : "assurance-" + s.id}/`}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative md:col-span-3 ${s.span} overflow-hidden rounded-3xl border hairline bg-cardbg`}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cardbg via-cardbg/20 to-transparent" />
                <span className="absolute top-4 left-4 rounded-full glass px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
                  {s.badge}
                </span>
              </div>
              <div className="p-7 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif text-2xl font-semibold text-ivory">
                    {s.title}
                  </h3>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-ivory/30" />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ivory/55">{s.description}</p>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {s.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex items-center gap-1.5 rounded-full border hairline px-3 py-1 text-xs text-ivory/60"
                    >
                      <Check className="h-3 w-3 text-amber" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
